import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Utils, authJsonHeaders, handleSessionExpired } from '../../utils';
import { MdSave, MdLightbulbOutline } from 'react-icons/md';

const AdminPages = () => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('terms');

    const pages = [
        { key: 'terms', label: 'Terms & Conditions' },
        { key: 'privacy', label: 'Privacy Policy' },
        { key: 'about', label: 'About Us' },
        { key: 'contact', label: 'Contact Info' }
    ];

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await fetch(Utils.baseurl + 'admin/settings', {
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                const settingsObj = {};
                pages.forEach(p => {
                    settingsObj[p.key] = result.data[p.key]?.value || '';
                });
                setSettings(settingsObj);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
        setLoading(false);
    };

    const handleSave = async (key) => {
        setSaving(true);
        try {
            const response = await fetch(Utils.baseurl + 'admin/settings/save', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ key, value: settings[key], type: 'html' })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                toast.success('Saved successfully!');
            }
        } catch (error) {
            console.error('Error saving:', error);
            toast.error('Error saving. Please try again.');
        }
        setSaving(false);
    };

    if (loading) {
        return <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>;
    }

    return (
        <div>
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Pages & Content</h1>
                <p style={{ color: '#6b7280', marginTop: '8px' }}>Manage static pages content</p>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>
                {pages.map(page => (
                    <button
                        key={page.key}
                        onClick={() => setActiveTab(page.key)}
                        style={{
                            padding: '10px 20px', border: 'none', borderRadius: '8px 8px 0 0',
                            cursor: 'pointer', fontWeight: '500', fontSize: '14px',
                            background: activeTab === page.key ? '#4f46e5' : 'transparent',
                            color: activeTab === page.key ? 'white' : '#6b7280'
                        }}
                    >
                        {page.label}
                    </button>
                ))}
            </div>

            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                        {pages.find(p => p.key === activeTab)?.label}
                    </label>
                    <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '12px' }}>
                        You can use HTML tags for formatting. This content will be displayed on the public page.
                    </p>
                </div>

                <textarea
                    value={settings[activeTab] || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, [activeTab]: e.target.value }))}
                    style={{
                        width: '100%', minHeight: '400px', padding: '16px',
                        border: '1px solid #d1d5db', borderRadius: '8px',
                        fontSize: '14px', fontFamily: 'monospace', resize: 'vertical',
                        boxSizing: 'border-box'
                    }}
                    placeholder={`Enter ${pages.find(p => p.key === activeTab)?.label} content here...`}
                />

                <div style={{ marginTop: '16px' }}>
                    <h4 style={{ margin: '0 0 8px 0', color: '#374151' }}>Preview:</h4>
                    <div
                        style={{
                            padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px',
                            background: '#f9fafb', minHeight: '100px', maxHeight: '300px', overflow: 'auto'
                        }}
                        dangerouslySetInnerHTML={{ __html: settings[activeTab] || '<p style="color: #9ca3af;">No content yet</p>' }}
                    />
                </div>

                <button
                    onClick={() => handleSave(activeTab)}
                    disabled={saving}
                    style={{
                        marginTop: '20px', display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '12px 24px', background: '#4f46e5', color: 'white',
                        border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer',
                        fontWeight: '500', opacity: saving ? 0.7 : 1
                    }}
                >
                    <MdSave size={18} />
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            <div style={{ marginTop: '24px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MdLightbulbOutline />
                    Tips
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400e', fontSize: '13px' }}>
                    <li>Use <code>&lt;h1&gt;</code>, <code>&lt;h2&gt;</code>, <code>&lt;p&gt;</code> tags for structure</li>
                    <li>Use <code>&lt;ul&gt;</code> and <code>&lt;li&gt;</code> for lists</li>
                    <li>Use <code>&lt;strong&gt;</code> or <code>&lt;b&gt;</code> for bold text</li>
                    <li>Use <code>&lt;a href="..."&gt;</code> for links</li>
                </ul>
            </div>
        </div>
    );
};

export default AdminPages;
